import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ShieldCheck, Truck, Gift, Ban } from "lucide-react";

// Risk-reversal / reassurance band — the strongest last-mile CRO lever for
// a sensitive-skin line. Placed right after Best Sellers (peak intent) and
// echoed as a slim strip above the Final CTA.
const ITEMS = [
  { Icon: ShieldCheck, title: "60-day money-back guarantee", sub: "Love your results or your money back — no questions asked." },
  { Icon: Truck, title: "Free shipping over $69", sub: "Fast, tracked delivery across North America." },
  { Icon: Gift, title: "Free samples, every order", sub: "Discover more of the routine with each purchase." },
  { Icon: Ban, title: "Cancel anytime", sub: "No lock-ins on subscriptions — you're always in control." },
];

export const GuaranteeBand = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <section ref={ref} className="bg-white py-16 lg:py-20 border-y border-neutral-200" data-testid="home-guarantee">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
        {ITEMS.map(({ Icon, title, sub }, i) => (
          <motion.div
            key={title}
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 }}
            className="flex flex-col items-start gap-4"
            data-testid={`guarantee-item-${i}`}
          >
            <span className="w-12 h-12 rounded-full bg-riversol-blue/[0.06] flex items-center justify-center text-riversol-blue shrink-0">
              <Icon size={20} strokeWidth={1.7} />
            </span>
            <div>
              <div className="text-[15px] lg:text-[16px] font-medium text-neutral-900 leading-snug">{title}</div>
              <p className="mt-1.5 text-[13px] text-neutral-500 leading-relaxed max-w-[15rem]">{sub}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

// Slim inline echo — sits just above the Final CTA.
export const GuaranteeStrip = () => (
  <div className="bg-riversol-paper border-t border-neutral-200" data-testid="home-guarantee-strip">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 font-mono text-[11px] tracking-[0.14em] uppercase text-neutral-500">
      {ITEMS.map(({ Icon, title }) => (
        <span key={title} className="inline-flex items-center gap-2">
          <Icon size={14} strokeWidth={1.8} className="text-riversol-blue" />
          {title}
        </span>
      ))}
    </div>
  </div>
);

export default GuaranteeBand;
